import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { fetchContacts } from '../store/slices/contactSlice';
import { fetchTemplates } from '../store/slices/templateSlice';
import { fetchFooters } from '../store/slices/footerSlice';
import { toast } from 'react-toastify';
import api from '../utils/api';
import {
  PaperAirplaneIcon,
  UserIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  EyeIcon,
  PhotoIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import TemplateDesigner from '../components/TemplateDesigner';
import CountrySelect from '../components/CountrySelect';
import Pagination from '../components/Pagination';
import ConfirmClearRecipientsDialog from '../components/ConfirmClearRecipientsDialog';
import UserSearchSelect from '../components/UserSearchSelect';
import { INSTITUTION_TYPES } from '../utils/constants';

const ComposeEmail = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { items: contacts, loading: contactsLoading } = useSelector((state) => state.contacts);
  const { items: templates } = useSelector((state) => state.templates);
  const { items: footers } = useSelector((state) => state.footers);
  const { user } = useSelector((state) => state.auth);

  // Form state
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [emailData, setEmailData] = useState({
    to: '',
    subject: '',
    content: '',
    templateId: '',
    footerId: '',
    designJson: null
  });
  const [showDesigner, setShowDesigner] = useState(false);
  const [editorStyles, setEditorStyles] = useState({
    backgroundColor: '#ffffff',
    contentWidth: '100%',
    textAlign: 'left',
    fontFamily: 'Arial, sans-serif',
    wrapperBackground: '#f3f4f6'
  });
  const [sending, setSending] = useState(false);

  // Contact selection state
  const [showContactModal, setShowContactModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showClearConfirmDialog, setShowClearConfirmDialog] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedInstitution, setSelectedInstitution] = useState('');
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [priorityFilter, setPriorityFilter] = useState('');
  const [relationshipFilter, setRelationshipFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [blockCodeFilter, setBlockCodeFilter] = useState('');
  const [createdByFilter, setCreatedByFilter] = useState(''); // NEW: Added By filter
  const [dateFromFilter, setDateFromFilter] = useState(''); // NEW: From Date filter
  const [dateToFilter, setDateToFilter] = useState(''); // NEW: To Date filter
  const [excludeSentFilter, setExcludeSentFilter] = useState(false); // NEW: Exclude already sent contacts
  const [showFilters, setShowFilters] = useState(true); // NEW: Toggle filters visibility
  
  // Pagination state for contact modal
  const [modalCurrentPage, setModalCurrentPage] = useState(1);
  const [modalItemsPerPage, setModalItemsPerPage] = useState(50);
  const [modalTotalContacts, setModalTotalContacts] = useState(0);
  const [modalTotalPages, setModalTotalPages] = useState(0);
  const [modalLoading, setModalLoading] = useState(false);

  // Ref to prevent duplicate contact loading in StrictMode
  const loadingContactRef = useRef(null);
  const contentRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showAllRecipients, setShowAllRecipients] = useState(false);
  const [removingRecipient, setRemovingRecipient] = useState(null);
  const [freeEmailInput, setFreeEmailInput] = useState(''); // for typing unsaved emails

  // Simple image upload handler
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/templates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = `http://localhost:5009${response.data.file.path}`;

      // Create image element with resize functionality
      const imageId = `img-${Date.now()}`;

      // Create the container div
      const container = document.createElement('div');
      container.className = 'image-resize-container';
      container.setAttribute('data-image-id', imageId);

      // Create size controls
      const sizeControls = document.createElement('div');
      sizeControls.className = 'image-size-controls';
      sizeControls.innerHTML = `
        <button class="size-button" data-size="25">25%</button>
        <button class="size-button" data-size="50">50%</button>
        <button class="size-button active" data-size="100">100%</button>
      `;

      // Create image
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = 'Uploaded image';
      img.style.width = '100%';
      img.style.height = 'auto';
      img.setAttribute('data-image-id', imageId);

      // Create resize handles
      const handles = ['nw', 'ne', 'sw', 'se'];
      const resizeHandles = handles.map(position => {
        const handle = document.createElement('div');
        handle.className = `resize-handle ${position}`;
        return handle;
      });

      // Append all elements
      container.appendChild(sizeControls);
      container.appendChild(img);
      resizeHandles.forEach(handle => container.appendChild(handle));

      // Insert into editor
      if (contentRef.current) {
        contentRef.current.appendChild(container);

        // Setup event listeners for this specific image
        setupImageListeners(container);

        // Update content state
        setEmailData(prev => ({ ...prev, content: contentRef.current.innerHTML }));
      }

      toast.success('Image uploaded successfully');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error('Failed to upload image');
    }
  };

  // Setup listeners for a specific image container
  const setupImageListeners = (container) => {
    const img = container.querySelector('img');
    const sizeButtons = container.querySelectorAll('.size-button');
    const resizeHandles = container.querySelectorAll('.resize-handle');

    // Image selection
    img.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Remove selection from all containers
      const allContainers = contentRef.current.querySelectorAll('.image-resize-container');
      allContainers.forEach(c => c.classList.remove('selected'));

      // Select this container
      container.classList.add('selected');
      setSelectedImage(img);
    });

    // Size button handlers
    sizeButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const size = button.getAttribute('data-size');
        img.style.width = size + '%';

        // Update active button
        sizeButtons.forEach(btn => btn.classList.remove('active'));
        button.classList.add('active');

        handleContentChange();
      });
    });

    // Drag resize handlers
    resizeHandles.forEach(handle => {
      handle.addEventListener('mousedown', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const startX = e.clientX;
        const startWidth = img.offsetWidth;
        const containerWidth = contentRef.current.offsetWidth - 32; // Account for padding

        const handleMouseMove = (e) => {
          const deltaX = e.clientX - startX;
          let newWidth = startWidth;

          if (handle.classList.contains('se') || handle.classList.contains('ne')) {
            newWidth = startWidth + deltaX;
          } else if (handle.classList.contains('sw') || handle.classList.contains('nw')) {
            newWidth = startWidth - deltaX;
          }

          // Constrain width
          newWidth = Math.max(100, Math.min(newWidth, containerWidth));

          img.style.width = newWidth + 'px';

          // Update size buttons
          const percentage = Math.round((newWidth / containerWidth) * 100);
          sizeButtons.forEach(btn => {
            btn.classList.remove('active');
            const btnSize = parseInt(btn.getAttribute('data-size'));
            if (Math.abs(btnSize - percentage) < 15) {
              btn.classList.add('active');
            }
          });
        };

        const handleMouseUp = () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          handleContentChange();
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      });
    });
  };

  // Setup image resize and selection listeners for all images
  const setupImageResizeListeners = () => {
    if (!contentRef.current) return;

    const containers = contentRef.current.querySelectorAll('.image-resize-container');
    containers.forEach(container => {
      // Skip if already has listeners
      if (container.hasAttribute('data-listeners-attached')) return;

      setupImageListeners(container);
      container.setAttribute('data-listeners-attached', 'true');
    });
  };

  // Click outside to deselect images
  const handleEditorClick = (e) => {
    if (!e.target.closest('.image-resize-container')) {
      const containers = contentRef.current?.querySelectorAll('.image-resize-container');
      containers?.forEach(container => container.classList.remove('selected'));
      setSelectedImage(null);
    }
  };

  // Simple formatting functions
  const applyFormat = (command, value = null) => {
    document.execCommand(command, false, value);
    // Update content after formatting
    if (contentRef.current) {
      setEmailData(prev => ({ ...prev, content: contentRef.current.innerHTML }));
    }
  };

  const handleContentChange = () => {
    if (contentRef.current) {
      setEmailData(prev => ({ ...prev, content: contentRef.current.innerHTML }));
    }
  };

  // Sync content when emailData.content changes (from template selection)
  useEffect(() => {
    if (contentRef.current && contentRef.current.innerHTML !== emailData.content) {
      contentRef.current.innerHTML = emailData.content || '';
      // Setup listeners after content is loaded
      setTimeout(() => {
        setupImageResizeListeners();
      }, 100);
    }
  }, [emailData.content]);

  // Setup listeners when component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setupImageResizeListeners();
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Get user's accessible institution types
  const userInstitutionTypes = user?.role === 'superadmin'
    ? INSTITUTION_TYPES
    : (user?.institutionTypes || []);

  // LOAD DATA OPTIMIZATION: Split effects to prevent redundant fetching
  // 1. Initial Data Load - Run only once on mount
  useEffect(() => {
    dispatch(fetchTemplates());
    dispatch(fetchFooters());
  }, [dispatch]);

  // 2. Apply user's default template and footer after data loads
  const defaultsAppliedRef = useRef(false);
  useEffect(() => {
    if (!user || defaultsAppliedRef.current) return;
    // Wait for both templates AND footers to load before applying defaults
    if (templates.length === 0 || footers.length === 0) return;

    const defaultTemplateId = user.defaultTemplateId;
    const defaultFooterId = user.defaultFooterId;

    console.log('🔍 User Default Settings:', {
      userId: user.id,
      userName: user.name,
      role: user.role,
      defaultTemplateId,
      defaultFooterId,
      templatesCount: templates.length,
      footersCount: footers.length,
      currentFooterId: emailData.footerId
    });

    if (!defaultTemplateId && !defaultFooterId) {
      defaultsAppliedRef.current = true;
      return;
    }

    // Only apply defaults if no template/footer is already selected (e.g. from URL params)
    let applied = false;

    if (defaultTemplateId && templates.length > 0 && !emailData.templateId) {
      const defaultTemplate = templates.find(t => t.id == defaultTemplateId);
      if (defaultTemplate) {
        // Apply template selection inline (same logic as handleTemplateSelect)
        const templateFooterId = defaultTemplate.useFooterTemplate && defaultTemplate.footerTemplateId
          ? defaultTemplate.footerTemplateId.toString()
          : '';
        const hasPlaceholder = defaultTemplate.htmlContent && defaultTemplate.htmlContent.includes('[[EMAIL_CONTENT]]');

        const design = defaultTemplate.designJson;
        const bodyValues = design?.body?.values || {};

        setEditorStyles({
          backgroundColor: bodyValues.backgroundColor || '#ffffff',
          contentWidth: bodyValues.contentWidth || '100%',
          fontFamily: bodyValues.fontFamily?.value || 'Arial, sans-serif',
          textAlign: hasPlaceholder && defaultTemplate.htmlContent.includes('text-align: center') ? 'center' : 'left',
          wrapperBackground: bodyValues.backgroundColor || '#f3f4f6'
        });

        let newContent = hasPlaceholder ? '' : (defaultTemplate.htmlContent || '');
        if (hasPlaceholder && defaultTemplate.htmlContent.includes('text-align: center') && !newContent) {
          newContent = '<div style="text-align: center;"><br></div>';
        }

        // Priority: template footer > user default footer > empty
        let finalFooterId = '';
        if (templateFooterId) {
          finalFooterId = templateFooterId;
        } else if (defaultFooterId) {
          const footerExists = footers.find(f => f.id == defaultFooterId);
          if (footerExists && footerExists.isActive !== false) {
            finalFooterId = String(defaultFooterId);
          }
        }

        console.log('🔍 Initial Load Footer Logic (with template):', {
          templateFooterId,
          defaultFooterId,
          footerExists: footers.find(f => f.id == defaultFooterId),
          finalFooterId
        });

        setEmailData(prev => ({
          ...prev,
          templateId: defaultTemplate.id.toString(),
          subject: defaultTemplate.subject || prev.subject,
          content: newContent,
          footerId: finalFooterId,
          designJson: defaultTemplate.designJson || null
        }));
        applied = true;
      }
    }

    // Apply default footer even if no template was applied
    if (defaultFooterId && footers.length > 0 && !emailData.footerId) {
      const footerExists = footers.find(f => f.id == defaultFooterId);
      console.log('🔍 Footer-only default logic:', {
        defaultFooterId,
        footerExists,
        footerIsActive: footerExists?.isActive
      });
      
      if (footerExists && footerExists.isActive !== false) {
        const footerIdString = String(defaultFooterId);
        console.log('✅ Setting footer to:', footerIdString);
        setEmailData(prev => {
          console.log('📝 Before setState:', { prevFooterId: prev.footerId, newFooterId: footerIdString });
          return { ...prev, footerId: footerIdString };
        });
        applied = true;
      }
    }

    defaultsAppliedRef.current = true;
  }, [user, templates, footers]); // eslint-disable-line

  // 2. URL Parameter Handling - Run only when query params change
  useEffect(() => {
    const contactId = searchParams.get('contactId');

    // Check if we need to load a contact (and haven't already loaded this specific ID)
    if (contactId && loadingContactRef.current !== contactId) {
      loadingContactRef.current = contactId;

      let isCancelled = false;

      const loadContact = async () => {
        try {
          const response = await api.get(`/contacts/${contactId}`);

          if (!isCancelled) {
            const contact = response.data;
            const recipient = {
              id: contact.id,
              name: contact.primaryName || contact.name,
              // Use ALL selected emails
              email: Array.isArray(contact.emails) && contact.emails.length > 0 
                ? contact.emails.join(',') 
                : (contact.email || ''),
              institutionType: contact.institutionType,
              country: contact.country
            };
            
            setSelectedRecipients([recipient]);
            setEmailData(prev => ({
              ...prev,
              to: recipient.email
            }));
            toast.success(`Contact "${contact.primaryName || contact.name}" loaded successfully`);
          }
        } catch (error) {
          if (!isCancelled) {
            console.error('Failed to load contact:', error);
            if (error.response?.status === 404) {
              toast.error('Contact not found. It may have been deleted.');
            } else if (error.response?.status === 403) {
              toast.error('You do not have permission to access this contact.');
            } else {
              toast.error('Failed to load contact. Please try again.');
            }
          }
        }
      };

      loadContact();

      return () => {
        isCancelled = true;
      };
    }
  }, [searchParams]);

  useEffect(() => {
    // Fetch contacts with filters and pagination when modal is open
    if (showContactModal) {
      const fetchModalContacts = async () => {
        setModalLoading(true);
        try {
          const filters = {
            page: modalCurrentPage,
            limit: modalItemsPerPage
          };

          if (selectedInstitution) filters.institutionType = selectedInstitution;
          if (priorityFilter) filters.priority = priorityFilter;
          if (relationshipFilter) filters.relationshipStage = relationshipFilter;
          if (countryFilter) filters.country = countryFilter;
          if (blockCodeFilter) filters.blockCode = blockCodeFilter;
          if (contactSearch) filters.search = contactSearch;
          if (createdByFilter) filters.createdBy = createdByFilter; // NEW: Added By filter
          if (dateFromFilter) filters.dateFrom = dateFromFilter; // NEW: From Date filter
          if (dateToFilter) filters.dateTo = dateToFilter; // NEW: To Date filter
          if (excludeSentFilter) filters.excludeSentSubject = emailData.subject; // NEW: Exclude already sent contacts

          const response = await api.get('/contacts', { params: filters });
          
          setFilteredContacts(response.data.contacts || []);
          setModalTotalContacts(response.data.total || 0);
          setModalTotalPages(response.data.pages || 0);
        } catch (error) {
          console.error('Failed to fetch contacts:', error);
          toast.error('Failed to load contacts');
        } finally {
          setModalLoading(false);
        }
      };

      fetchModalContacts();
    }
  }, [showContactModal, modalCurrentPage, modalItemsPerPage, selectedInstitution, priorityFilter, relationshipFilter, countryFilter, blockCodeFilter, contactSearch, createdByFilter, dateFromFilter, dateToFilter, excludeSentFilter, emailData.subject]);

  // Reset to first page when filters change (not when modal opens/closes or page changes)
  useEffect(() => {
    setModalCurrentPage(1);
  }, [selectedInstitution, priorityFilter, relationshipFilter, countryFilter, blockCodeFilter, contactSearch, createdByFilter, dateFromFilter, dateToFilter, excludeSentFilter]);

  // Clear all filters helper function
  const handleClearAllFilters = () => {
    setSelectedInstitution('');
    setPriorityFilter('');
    setRelationshipFilter('');
    setCountryFilter('');
    setBlockCodeFilter('');
    setContactSearch('');
    setCreatedByFilter('');
    setDateFromFilter('');
    setDateToFilter('');
    setExcludeSentFilter(false);
    setModalCurrentPage(1);
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return selectedInstitution || priorityFilter || relationshipFilter || 
           countryFilter || blockCodeFilter || contactSearch || createdByFilter || 
           dateFromFilter || dateToFilter || excludeSentFilter;
  };

  const handleContactSelect = (contact) => {
    const recipient = {
      id: contact.id,
      name: contact.primaryName,
      // Use ALL selected emails, not just the first one
      email: Array.isArray(contact.emails) && contact.emails.length > 0
        ? contact.emails.join(',')
        : (contact.email || ''),
      institutionType: contact.institutionType,
      country: contact.country
    };
    
    setSelectedRecipients([recipient]);
    setEmailData(prev => ({
      ...prev,
      to: recipient.email
    }));
    setShowContactModal(false);
  };

  const handleToggleContact = (contact) => {
    setSelectedContacts(prev => {
      const exists = prev.find(c => c.id === contact.id);
      if (exists) {
        return prev.filter(c => c.id !== contact.id);
      } else {
        return [...prev, contact];
      }
    });
  };

  const handleSelectAll = () => {
    // Add current page contacts to existing selections (don't replace)
    setSelectedContacts(prev => {
      const newContacts = filteredContacts.filter(contact => 
        !prev.find(c => c.id === contact.id)
      );
      return [...prev, ...newContacts];
    });
  };

  const handleSelectAllMatching = async () => {
    try {
      setModalLoading(true);
      
      // Fetch ALL contacts matching current filters (no pagination limit)
      const filters = {};
      if (selectedInstitution) filters.institutionType = selectedInstitution;
      if (priorityFilter) filters.priority = priorityFilter;
      if (relationshipFilter) filters.relationshipStage = relationshipFilter;
      if (countryFilter) filters.country = countryFilter;
      if (contactSearch) filters.search = contactSearch;
      if (blockCodeFilter) filters.blockCode = blockCodeFilter;
      if (createdByFilter) filters.createdBy = createdByFilter;
      if (dateFromFilter) filters.dateFrom = dateFromFilter;
      if (dateToFilter) filters.dateTo = dateToFilter;
      if (excludeSentFilter) filters.excludeSentSubject = emailData.subject;
      
      // Request all contacts (use a very high limit)
      filters.limit = 10000;
      filters.page = 1;

      const response = await api.get('/contacts', { params: filters });
      const allMatchingContacts = response.data.contacts || [];
      
      setSelectedContacts(allMatchingContacts);
      toast.success(`Selected all ${allMatchingContacts.length} matching contacts`);
    } catch (error) {
      console.error('Failed to fetch all contacts:', error);
      toast.error('Failed to select all contacts');
    } finally {
      setModalLoading(false);
    }
  };

  const handleDeselectAll = () => {
    setSelectedContacts([]);
  };

  const handleConfirmSelection = () => {
    if (selectedContacts.length === 0) {
      toast.error('Please select at least one contact');
      return;
    }

    // Map all selected contacts to recipients
    const recipients = selectedContacts.map(c => ({
      id: c.id,
      name: c.primaryName,
      // Use ALL selected emails for each contact
      email: Array.isArray(c.emails) && c.emails.length > 0 
        ? c.emails.join(',') 
        : (c.email || ''),
      institutionType: c.institutionType,
      country: c.country
    })).filter(r => r.email);

    // Merge with existing recipients (avoid duplicates)
    const existingEmails = new Set(selectedRecipients.map(r => r.email));
    const newRecipients = recipients.filter(r => !existingEmails.has(r.email));
    const allRecipients = [...selectedRecipients, ...newRecipients];

    setSelectedRecipients(allRecipients);
    
    const emails = allRecipients.map(r => r.email).join(',');
    setEmailData(prev => ({ ...prev, to: emails }));
    
    setShowContactModal(false);
    toast.success(`${newRecipients.length} new contact${newRecipients.length !== 1 ? 's' : ''} added`);
    
    setSelectedContacts([]);
  };

  const handleRemoveRecipient = async (emailToRemove) => {
    setRemovingRecipient(emailToRemove);
    
    // Simulate smooth removal with slight delay for UX
    await new Promise(resolve => setTimeout(resolve, 200));
    
    const updatedRecipients = selectedRecipients.filter(r => r.email !== emailToRemove);
    setSelectedRecipients(updatedRecipients);
    
    if (updatedRecipients.length === 0) {
      setEmailData(prev => ({ ...prev, to: '' }));
      setShowAllRecipients(false);
    } else {
      const emails = updatedRecipients.map(r => r.email).join(',');
      setEmailData(prev => ({ ...prev, to: emails }));
    }
    
    setRemovingRecipient(null);
    toast.success('Recipient removed');
  };

  const handleManualEmailChange = (value) => {
    setEmailData(prev => ({ ...prev, to: value }));
    
    // Clear recipients when manually editing
    if (value === '') {
      setSelectedRecipients([]);
    }
  };

  const handleFreeEmailInput = (e) => {
    const value = e.target.value;
    setFreeEmailInput(value);
  };

  const addFreeEmail = (email) => {
    const trimmedEmail = email.trim();
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    // Check if email already exists
    const existingEmails = selectedRecipients.map(r => r.email.toLowerCase());
    if (existingEmails.includes(trimmedEmail.toLowerCase())) {
      toast.warning('This email is already added');
      setFreeEmailInput('');
      return;
    }

    // Add as a new recipient
    const newRecipient = {
      id: `free-${Date.now()}`,
      email: trimmedEmail,
      name: trimmedEmail.split('@')[0], // Use part before @ as name
      isFreeEmail: true
    };

    const updatedRecipients = [...selectedRecipients, newRecipient];
    setSelectedRecipients(updatedRecipients);
    
    const emails = updatedRecipients.map(r => r.email).join(',');
    setEmailData(prev => ({ ...prev, to: emails }));
    
    setFreeEmailInput('');
    toast.success('Email added');
  };

  const handleFreeEmailKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') {
      e.preventDefault();
      if (freeEmailInput.trim()) {
        addFreeEmail(freeEmailInput);
      }
    } else if (e.key === 'Backspace' && freeEmailInput === '' && selectedRecipients.length > 0) {
      // Remove last recipient if backspace on empty input
      const lastRecipient = selectedRecipients[selectedRecipients.length - 1];
      handleRemoveRecipient(lastRecipient.email);
    }
  };

  const handleClearAllRecipients = async () => {
    // Simulate async operation (in case you want to add API call later)
    return new Promise((resolve) => {
      setTimeout(() => {
        setSelectedRecipients([]);
        setEmailData(prev => ({ ...prev, to: '' }));
        setFreeEmailInput('');
        setShowClearConfirmDialog(false);
        setShowAllRecipients(false);
        toast.success('All recipients cleared successfully');
        resolve();
      }, 300);
    });
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === parseInt(templateId));
    if (template) {
      const templateFooterId = template.useFooterTemplate && template.footerTemplateId
        ? template.footerTemplateId.toString()
        : '';
      const hasPlaceholder = template.htmlContent && template.htmlContent.includes('[[EMAIL_CONTENT]]');

      // Extract design settings
      const design = template.designJson;
      const bodyValues = design?.body?.values || {};

      setEditorStyles({
        backgroundColor: bodyValues.backgroundColor || '#ffffff',
        contentWidth: bodyValues.contentWidth || '100%',
        fontFamily: bodyValues.fontFamily?.value || 'Arial, sans-serif',
        textAlign: hasPlaceholder && template.htmlContent.includes('text-align: center') ? 'center' : 'left',
        wrapperBackground: bodyValues.backgroundColor || '#f3f4f6'
      });

      setEmailData(prev => {
        const wasFullTemplate = prev.templateId && !templates.find(t => t.id === parseInt(prev.templateId))?.htmlContent?.includes('[[EMAIL_CONTENT]]');
        let newContent = hasPlaceholder ? (wasFullTemplate ? '' : prev.content) : (template.htmlContent || prev.content);

        // If template is centered, ensure content starts centered
        if (hasPlaceholder && template.htmlContent.includes('text-align: center') && !newContent) {
          newContent = '<div style="text-align: center;"><br></div>';
        }

        // Use template footer, or fallback to user's default footer, or empty
        const finalFooterId = templateFooterId || 
          (user?.defaultFooterId && footers.find(f => f.id == user.defaultFooterId && f.isActive !== false)
            ? user.defaultFooterId.toString()
            : '');

        console.log('🔍 Manual Template Select Footer Logic:', {
          templateId,
          templateFooterId,
          userDefaultFooterId: user?.defaultFooterId,
          userDefaultFooterIdType: typeof user?.defaultFooterId,
          footersIds: footers.map(f => ({ id: f.id, type: typeof f.id, isActive: f.isActive })),
          foundFooter: footers.find(f => f.id == user?.defaultFooterId && f.isActive !== false),
          finalFooterId
        });

        return {
          ...prev,
          templateId,
          subject: template.subject || prev.subject,
          content: newContent,
          footerId: finalFooterId,
          designJson: template.designJson || null
        };
      });
    } else {
      // Reset template — clear footer, let user choose
      setEditorStyles({
        backgroundColor: '#ffffff',
        contentWidth: '100%',
        textAlign: 'left',
        fontFamily: 'Arial, sans-serif',
        wrapperBackground: '#f3f4f6'
      });
      setEmailData(prev => ({
        ...prev,
        templateId: '',
        content: '',
        footerId: '',
        designJson: null
      }));
    }
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();

    if (sending) return;

    if (!emailData.to || !emailData.subject || !emailData.content) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    try {
      // Prepare email content with footer
      let htmlContent = emailData.content;

      // Add footer if selected
      if (emailData.footerId) {
        const footer = footers.find(f => f.id === parseInt(emailData.footerId));
        if (footer) {
          htmlContent += `${footer.htmlContent}`;
        }
      } else if (emailData.templateId) {
        // If no footer template is selected, check if template has its own custom footer text
        const selectedTemplate = templates.find(t => t.id === parseInt(emailData.templateId));
        if (selectedTemplate && selectedTemplate.footer && !selectedTemplate.useFooterTemplate) {
          htmlContent += `${selectedTemplate.footer}`;
        }
      }

      // If template acts as a frame, inject the content into the frame
      const selectedTemplate = templates.find(t => t.id === parseInt(emailData.templateId));
      if (selectedTemplate && selectedTemplate.htmlContent && selectedTemplate.htmlContent.includes('[[EMAIL_CONTENT]]')) {
        htmlContent = selectedTemplate.htmlContent.replace('[[EMAIL_CONTENT]]', emailData.content || '');
      }

      const response = await api.post('/email/send', {
        to: emailData.to,
        subject: emailData.subject,
        html: htmlContent,
        contactId: selectedRecipients.length === 1 ? selectedRecipients[0].id : null,
        templateId: emailData.templateId || null
      });

      toast.success('Email sent successfully!');

      // Reset form
      setEmailData({
        to: '',
        subject: '',
        content: '',
        templateId: '',
        footerId: ''
      });
      setSelectedRecipients([]);

      // Navigate based on user role
      // Super Admin can see activity logs, others go to dashboard
      if (user?.role === 'superadmin') {
        navigate('/activity-logs');
      } else {
        navigate('/dashboard');
      }

    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const handlePreviewEmail = () => {
    if (!emailData.subject || !emailData.content) {
      toast.error('Please fill in subject and content to preview');
      return;
    }
    setShowPreviewModal(true);
  };

  const getPreviewContent = () => {
    let htmlContent = emailData.content;

    // Add footer if selected
    if (emailData.footerId) {
      const footer = footers.find(f => f.id === parseInt(emailData.footerId));
      if (footer) {
        htmlContent += `${footer.htmlContent}`;
      }
    } else if (emailData.templateId) {
      // If no footer template is selected, check if template has its own custom footer text
      const selectedTemplate = templates.find(t => t.id === parseInt(emailData.templateId));
      if (selectedTemplate && selectedTemplate.footer && !selectedTemplate.useFooterTemplate) {
        htmlContent += `${selectedTemplate.footer}`;
      }
    }

    // If template acts as a frame, inject the content into the frame
    const selectedTemplate = templates.find(t => t.id === parseInt(emailData.templateId));
    if (selectedTemplate && selectedTemplate.htmlContent && selectedTemplate.htmlContent.includes('[[EMAIL_CONTENT]]')) {
      htmlContent = selectedTemplate.htmlContent.replace('[[EMAIL_CONTENT]]', emailData.content || '');
    }

    return htmlContent;
  };

  const openContactSelector = () => {
    setShowContactModal(true);
    setModalCurrentPage(1); // Reset to first page
    // Don't clear previous selections - allow adding more
  };

  return (
    <div className="space-y-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Compose Email</h1>
          <p className="mt-2 text-sm sm:text-base text-gray-600 font-medium">
            Send personalized emails to individual contacts
          </p>
        </div>

        {/* Main Form */}
        <div className="bg-white shadow-xl rounded-2xl border border-gray-200">
          <form onSubmit={handleSendEmail} className="p-6 space-y-6">
            {/* Individual Sender Indicator */}
            {user?.sendEmailIndividually && user?.sendingEmail && (
              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-200 rounded-xl shadow-sm">
                <div className="flex items-center gap-3">
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full flex items-center justify-center">
                    <EnvelopeIcon className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-indigo-900">
                      Sending from your configured email
                    </p>
                    <p className="text-xs text-indigo-700 mt-0.5">
                      <span className="font-medium">From:</span> {user.sendingEmail}
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-indigo-100 text-indigo-800">
                      Individual Send
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Recipient Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Recipients * {selectedRecipients.length > 0 && (
                  <span className="text-blue-600 font-semibold">
                    ({selectedRecipients.length} selected)
                  </span>
                )}
              </label>
              
              {/* Recipients Display Area */}
              {selectedRecipients.length > 0 ? (
                <div className="mb-3 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                  
                  {/* Summary View for Many Recipients */}
                  {selectedRecipients.length > 5 ? (
                    <div className="space-y-3">
                      {/* Summary Card */}
                      <div className="bg-white rounded-lg p-4 border border-blue-300 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                              {selectedRecipients.length}
                            </div>
                            <div>
                              <p className="text-lg font-bold text-gray-900">
                                {selectedRecipients.length} Recipients Selected
                              </p>
                              <p className="text-sm text-gray-600">
                                {selectedRecipients.slice(0, 3).map(r => r.name).join(', ')}
                                {selectedRecipients.length > 3 && ` and ${selectedRecipients.length - 3} more...`}
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setShowAllRecipients(!showAllRecipients)}
                            className="text-sm text-blue-600 hover:text-blue-800 font-semibold px-4 py-2 rounded-lg hover:bg-blue-100 transition-all"
                          >
                            {showAllRecipients ? 'Hide Details' : 'View All'}
                          </button>
                        </div>
                      </div>

                      {/* Expandable List */}
                      {showAllRecipients && (
                        <div className="bg-white rounded-lg p-4 border border-blue-300 max-h-96 overflow-y-auto">
                          <div className="space-y-2">
                            {selectedRecipients.map((recipient, index) => (
                              <div
                                key={recipient.email}
                                className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg group"
                              >
                                <div className="flex items-center gap-3 flex-1 min-w-0">
                                  <span className="text-xs text-gray-500 font-medium w-8 flex-shrink-0">
                                    #{index + 1}
                                  </span>
                                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0">
                                    {recipient.name.charAt(0).toUpperCase()}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 truncate">
                                      {recipient.name}
                                    </p>
                                    <p className="text-xs text-gray-600 truncate">
                                      {recipient.email}
                                    </p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveRecipient(recipient.email)}
                                  disabled={removingRecipient === recipient.email}
                                  className="ml-2 text-gray-400 hover:text-red-600 hover:bg-red-50 p-1.5 rounded transition-all opacity-0 group-hover:opacity-100 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Remove recipient"
                                >
                                  {removingRecipient === recipient.email ? (
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                  ) : (
                                    <XMarkIcon className="h-4 w-4" />
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2 pt-2">
                        <button
                          type="button"
                          onClick={openContactSelector}
                          className="text-sm text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 hover:bg-blue-100 px-3 py-2 rounded-lg transition-all"
                        >
                          <UserIcon className="h-4 w-4" />
                          Add more recipients
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowClearConfirmDialog(true)}
                          className="text-sm text-red-600 hover:text-red-800 font-semibold flex items-center gap-1 hover:bg-red-50 px-3 py-2 rounded-lg transition-all"
                        >
                          <XMarkIcon className="h-4 w-4" />
                          Clear all
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Chip View for Few Recipients (≤5) */
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2 items-center">
                        {selectedRecipients.map((recipient) => (
                          <div
                            key={recipient.email}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border shadow-sm hover:shadow-md transition-all group ${
                              recipient.isFreeEmail
                                ? 'bg-amber-50 border-amber-300'
                                : 'bg-white border-blue-300'
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs ${
                                recipient.isFreeEmail
                                  ? 'bg-gradient-to-br from-amber-400 to-orange-500'
                                  : 'bg-gradient-to-br from-blue-500 to-indigo-600'
                              }`}>
                                {recipient.isFreeEmail ? (
                                  <EnvelopeIcon className="h-4 w-4" />
                                ) : (
                                  recipient.name.charAt(0).toUpperCase()
                                )}
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-900">
                                  {recipient.isFreeEmail ? recipient.email : recipient.name}
                                </span>
                                {!recipient.isFreeEmail && (
                                  <span className="text-xs text-gray-600">
                                    {recipient.email}
                                  </span>
                                )}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveRecipient(recipient.email)}
                              disabled={removingRecipient === recipient.email}
                              className="ml-2 text-gray-400 hover:text-red-600 hover:bg-red-50 p-1 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Remove recipient"
                            >
                              {removingRecipient === recipient.email ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                              ) : (
                                <XMarkIcon className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        ))}
                        {/* Inline input to add more emails without leaving the chip view */}
                        <input
                          type="text"
                          value={freeEmailInput}
                          onChange={handleFreeEmailInput}
                          onKeyDown={handleFreeEmailKeyDown}
                          onBlur={() => { if (freeEmailInput.trim()) addFreeEmail(freeEmailInput); }}
                          placeholder="Add email..."
                          className="px-3 py-2 text-sm border border-dashed border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 min-w-[150px] bg-white"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={openContactSelector}
                          className="text-sm text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-1 hover:bg-blue-100 px-3 py-2 rounded-lg transition-all"
                        >
                          <UserIcon className="h-4 w-4" />
                          Add from contacts
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowClearConfirmDialog(true)}
                          className="text-sm text-red-600 hover:text-red-800 font-semibold flex items-center gap-1 hover:bg-red-50 px-3 py-2 rounded-lg transition-all"
                        >
                          <XMarkIcon className="h-4 w-4" />
                          Clear all
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {/* Tag-style input: type any email + Select Contacts button */}
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1 min-h-[48px] px-3 py-2 border-2 border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-200 flex flex-wrap items-center gap-2 bg-white cursor-text"
                      onClick={() => document.getElementById('freeEmailInput').focus()}
                    >
                      <input
                        id="freeEmailInput"
                        type="text"
                        value={freeEmailInput}
                        onChange={handleFreeEmailInput}
                        onKeyDown={handleFreeEmailKeyDown}
                        onBlur={() => { if (freeEmailInput.trim()) addFreeEmail(freeEmailInput); }}
                        placeholder="Type email and press Enter, or select contacts →"
                        className="flex-1 min-w-[200px] outline-none border-none bg-transparent text-sm py-1"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={openContactSelector}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-5 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl transition-all duration-200 whitespace-nowrap"
                    >
                      <UserIcon className="h-5 w-5 mr-2" />
                      Select Contacts
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">
                    Press <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">Enter</kbd> or <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs">,</kbd> after each email to add it
                  </p>
                </div>
              )}
            </div>

            {/* Template Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Template (Optional)
              </label>
              <select
                value={emailData.templateId}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              >
                <option value="">Select a template...</option>
                {templates.map((template) => (
                  <option key={template.id} value={template.id}>
                    {template.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Subject *
              </label>
              <input
                type="text"
                value={emailData.subject}
                onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
                placeholder="Enter email subject"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                required
              />
            </div>

            {/* Content */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Email Content *
                </label>
              </div>

              {/* Simple Rich Text Toolbar */}
              <div className="border-2 border-gray-200 rounded-xl focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all duration-200 overflow-hidden">
                <div className="flex flex-wrap items-center gap-1 p-2 sm:p-3 border-b border-gray-200 bg-gray-50 rounded-t-xl overflow-x-auto scrollbar-hide">
                  <button
                    type="button"
                    onClick={() => applyFormat('bold')}
                    className="px-3 py-1 text-sm font-bold border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Bold"
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('italic')}
                    className="px-3 py-1 text-sm italic border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Italic"
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('underline')}
                    className="px-3 py-1 text-sm underline border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Underline"
                  >
                    U
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  {/* Heading Dropdown */}
                  <select
                    onChange={(e) => applyFormat('formatBlock', e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none"
                    value=""
                  >
                    <option value="" disabled selected>Heading</option>
                    <option value="<h1>">H1</option>
                    <option value="<h2>">H2</option>
                    <option value="<h3>">H3</option>
                    <option value="<p>">Paragraph</option>
                  </select>

                  {/* Font Size Dropdown */}
                  <select
                    onChange={(e) => applyFormat('fontSize', e.target.value)}
                    className="px-2 py-1 text-xs border border-gray-300 rounded bg-white hover:bg-gray-50 focus:outline-none"
                    value=""
                  >
                    <option value="" disabled selected>Size</option>
                    <option value="1">Smallest</option>
                    <option value="2">Small</option>
                    <option value="3">Normal</option>
                    <option value="4">Large</option>
                    <option value="5">Huge</option>
                  </select>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => applyFormat('undo')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Undo"
                  >
                    Undo
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('redo')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Redo"
                  >
                    Redo
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                  <button
                    type="button"
                    onClick={() => applyFormat('insertUnorderedList')}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Bullet List"
                  >
                    • List
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('insertOrderedList')}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Numbered List"
                  >
                    1. List
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                  <button
                    type="button"
                    onClick={() => applyFormat('justifyLeft')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Align Left"
                  >
                    Left
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('justifyCenter')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Align Center"
                  >
                    Center
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('justifyRight')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Align Right"
                  >
                    Right
                  </button>
                  <button
                    type="button"
                    onClick={() => applyFormat('justifyFull')}
                    className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-200 transition-colors"
                    title="Justify"
                  >
                    Justify
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-gray-500">Color:</span>
                    <input
                      type="color"
                      onChange={(e) => applyFormat('foreColor', e.target.value)}
                      className="w-6 h-6 p-0 border-0 bg-transparent cursor-pointer"
                      title="Text Color"
                    />
                  </div>
                  <div className="w-px h-6 bg-gray-300 mx-1"></div>
                  <label className="flex items-center px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-200 transition-colors cursor-pointer">
                    <PhotoIcon className="h-4 w-4 mr-1" />
                    Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Content Editor with Dynamic Frame */}
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ backgroundColor: editorStyles.wrapperBackground || '#f3f4f6' }}
                >
                  <div
                    className="mx-auto transition-all duration-300 shadow-sm"
                    style={{
                      maxWidth: editorStyles.contentWidth || '100%',
                      backgroundColor: editorStyles.backgroundColor || '#ffffff',
                      fontFamily: editorStyles.fontFamily || 'inherit'
                    }}
                  >
                    {/* Template Header Part */}
                    {emailData.templateId && templates.find(t => t.id === parseInt(emailData.templateId))?.htmlContent?.includes('[[EMAIL_CONTENT]]') && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: templates.find(t => t.id === parseInt(emailData.templateId)).htmlContent.split('[[EMAIL_CONTENT]]')[0]
                        }}
                      />
                    )}

                    <div
                      ref={contentRef}
                      contentEditable
                      onInput={handleContentChange}
                      onBlur={handleContentChange}
                      onClick={handleEditorClick}
                      className="simple-editor min-h-[300px] sm:min-h-[400px] p-4 sm:p-8 focus:outline-none"
                      style={{
                        lineHeight: 'normal',
                        fontSize: '16px',
                        textAlign: editorStyles.textAlign || 'left'
                      }}
                      placeholder="Type your message here..."
                    />

                    {/* Template Footer Part */}
                    {emailData.templateId && templates.find(t => t.id === parseInt(emailData.templateId))?.htmlContent?.includes('[[EMAIL_CONTENT]]') && (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: templates.find(t => t.id === parseInt(emailData.templateId)).htmlContent.split('[[EMAIL_CONTENT]]')[1]
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Selection - Always available */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Footer (Optional)
              </label>
              <select
                value={emailData.footerId}
                onChange={(e) => setEmailData(prev => ({ ...prev, footerId: e.target.value }))}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              >
                <option value="">No footer</option>
                {footers.filter(footer => footer.isActive).map((footer) => (
                  <option key={footer.id} value={footer.id}>
                    {footer.name} {footer.isDefault ? '(Default)' : ''}
                  </option>
                ))}
              </select>
            </div>

            {/* Actions */}
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-200 font-semibold transition-all duration-200 flex items-center justify-center"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handlePreviewEmail}
                className="bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-xl hover:from-green-700 hover:to-emerald-700 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <EyeIcon className="h-5 w-5 mr-2" />
                Preview
              </button>
              <button
                type="submit"
                disabled={sending}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 flex items-center justify-center font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                {sending ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Visual Designer Modal */}
      {showDesigner && (
        <div className="fixed inset-0 z-50">
          <TemplateDesigner
            initialContent={emailData.content}
            initialDesign={emailData.designJson}
            onContentChange={(html, design) => {
              setEmailData(prev => ({ ...prev, content: html, designJson: design }));
            }}
            onClose={() => setShowDesigner(false)}
            templateData={{ name: emailData.subject, subject: emailData.subject }}
            onTemplateDataChange={(data) => {
              if (data.subject) setEmailData(prev => ({ ...prev, subject: data.subject }));
            }}
            onSave={(html, design) => {
              setEmailData(prev => ({ ...prev, content: html, designJson: design }));
              setShowDesigner(false);
              toast.success('Design updated for this email');
            }}
          />
        </div>
      )}

      {/* Contact Selection Modal */}
      {showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-6xl w-full h-[90vh] flex flex-col shadow-2xl">
            
            {/* Fixed Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl flex-shrink-0">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-white">Select Contacts</h3>
                  <div className="flex items-center gap-4 mt-1">
                    <p className="text-sm text-blue-100">
                      {selectedContacts.length > 0 
                        ? `${selectedContacts.length} contact${selectedContacts.length > 1 ? 's' : ''} selected`
                        : 'Choose one or multiple contacts'}
                    </p>
                    {modalTotalContacts > 0 && (
                      <>
                        <span className="text-xs text-blue-200">•</span>
                        <p className="text-sm text-blue-100">
                          Page {modalCurrentPage} of {modalTotalPages} ({modalTotalContacts} total)
                        </p>
                      </>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowContactModal(false);
                    setSelectedContacts([]);
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Fixed Filters Section */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
              {/* Search Bar with Filter Toggle */}
              <div className="mb-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Search by Organization or Contact Person..."
                      className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
                  </div>
                  
                  {/* Filter Toggle Button */}
                  <button
                    type="button"
                    onClick={() => setShowFilters(!showFilters)}
                    className="px-4 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all text-sm font-semibold flex items-center gap-2 whitespace-nowrap shadow-md"
                    title={showFilters ? "Hide Filters" : "Show Filters"}
                  >
                    {showFilters ? (
                      <>
                        <ChevronUpIcon className="h-4 w-4" />
                        Hide Filters
                      </>
                    ) : (
                      <>
                        <ChevronDownIcon className="h-4 w-4" />
                        Show Filters
                      </>
                    )}
                  </button>
                </div>
              </div>

              {/* Collapsible Filters Section */}
              {showFilters && (
                <div className="space-y-3 mb-3">
                  {/* Filters Grid - Row 1: Main Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Institution Type:</label>
                      <select
                        value={selectedInstitution}
                        onChange={(e) => setSelectedInstitution(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">All Institution Types</option>
                        {userInstitutionTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Priority:</label>
                      <select
                        value={priorityFilter}
                        onChange={(e) => setPriorityFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">All Priorities</option>
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Relationship:</label>
                      <select
                        value={relationshipFilter}
                        onChange={(e) => setRelationshipFilter(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="">All Stages</option>
                        <option value="Cold">Cold</option>
                        <option value="Introduced">Introduced</option>
                        <option value="In discussion">In discussion</option>
                        <option value="MoU signed">MoU signed</option>
                        <option value="Active collaboration">Active collaboration</option>
                        <option value="Dormant">Dormant</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Country:</label>
                      <CountrySelect
                        value={countryFilter}
                        onChange={(countryName) => setCountryFilter(countryName)}
                        placeholder="All Countries"
                        isClearable={true}
                        className="text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Block Code:</label>
                      <input
                        type="text"
                        value={blockCodeFilter}
                        onChange={(e) => setBlockCodeFilter(e.target.value)}
                        placeholder="Search block code..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>
                  </div>

                  {/* Filters Grid - Row 2: Date & User Filters */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Added By:</label>
                      <UserSearchSelect
                        value={createdByFilter}
                        onChange={(userId) => setCreatedByFilter(userId)}
                        placeholder="All Users"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">From Date:</label>
                      <input
                        type="date"
                        value={dateFromFilter}
                        onChange={(e) => setDateFromFilter(e.target.value)}
                        max={dateToFilter || undefined}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">To Date:</label>
                      <input
                        type="date"
                        value={dateToFilter}
                        onChange={(e) => setDateToFilter(e.target.value)}
                        min={dateFromFilter || undefined}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                      />
                    </div>

                    <div className="flex items-center justify-start h-full pt-5">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={excludeSentFilter}
                          onChange={(e) => setExcludeSentFilter(e.target.checked)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        Exclude Sent (This Subject)
                      </label>
                    </div>

                    {/* Clear Filters Button - Shows only when filters are active */}
                    {hasActiveFilters() && (
                      <div className="flex items-end">
                        <button
                          type="button"
                          onClick={handleClearAllFilters}
                          className="w-full px-3 py-2 bg-gradient-to-r from-red-50 to-orange-50 text-red-700 border border-red-300 rounded-lg hover:from-red-100 hover:to-orange-100 transition-all text-sm font-semibold flex items-center justify-center gap-2"
                          title="Clear all filters"
                        >
                          <XMarkIcon className="h-4 w-4" />
                          Clear Filters
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Bulk Selection Buttons */}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
                >
                  Select Page ({filteredContacts.length})
                </button>
                <button
                  type="button"
                  onClick={handleSelectAllMatching}
                  className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg hover:from-blue-700 hover:to-indigo-700 transition-all text-xs font-medium"
                >
                  Select All ({modalTotalContacts})
                </button>
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-xs font-medium"
                >
                  Deselect All
                </button>
                {selectedContacts.length > 0 && (
                  <button
                    type="button"
                    onClick={handleConfirmSelection}
                    className="ml-auto px-4 py-1.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 transition-all text-xs font-semibold shadow-md"
                  >
                    Confirm Selection ({selectedContacts.length})
                  </button>
                )}
              </div>
            </div>

            {/* Scrollable Contact List */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {modalLoading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-3 text-sm">Loading contacts...</p>
                </div>
              ) : filteredContacts.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {filteredContacts.map((contact) => {
                    const isSelected = selectedContacts.find(c => c.id === contact.id);
                    return (
                      <div
                        key={contact.id}
                        onClick={() => handleToggleContact(contact)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={!!isSelected}
                            onChange={() => {}}
                            className="mt-1 w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">
                              {contact.primaryName}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">
                              {Array.isArray(contact.emails) && contact.emails.length > 0 
                                ? contact.emails.join(', ') 
                                : (contact.email || 'No email')
                              }
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                              <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full font-medium">
                                {contact.institutionType}
                              </span>
                              <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded-full font-medium">
                                {contact.country}
                              </span>
                              {contact.blockCode && (
                                <span className="text-xs px-2 py-1 bg-purple-100 text-purple-800 rounded-full font-medium">
                                  Block: {contact.blockCode}
                                </span>
                              )}
                              {contact.region && (
                                <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full font-medium">
                                  {contact.region}
                                </span>
                              )}
                              {contact.city && (
                                <span className="text-xs px-2 py-1 bg-teal-100 text-teal-800 rounded-full font-medium">
                                  {contact.city}
                                </span>
                              )}
                              {contact.priority && (
                                <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                  contact.priority === 'High' ? 'bg-red-100 text-red-800' :
                                  contact.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {contact.priority}
                                </span>
                              )}
                            </div>
                          </div>
                          <EnvelopeIcon className={`h-5 w-5 ${isSelected ? 'text-blue-600' : 'text-gray-400'}`} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No contacts found</p>
                  <p className="text-sm text-gray-400 mt-1">Try adjusting your filters</p>
                </div>
              )}
            </div>

            {/* Fixed Footer with Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 bg-white rounded-b-2xl flex-shrink-0">
              {modalTotalContacts > 0 && (
                <Pagination
                  currentPage={modalCurrentPage}
                  totalPages={modalTotalPages}
                  itemsPerPage={modalItemsPerPage}
                  totalItems={modalTotalContacts}
                  onPageChange={setModalCurrentPage}
                  onItemsPerPageChange={setModalItemsPerPage}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Email Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Email Preview</h3>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {/* Email Headers */}
              <div className="bg-gray-50 p-4 rounded-lg mb-6 space-y-2">
                <div className="flex">
                  <span className="font-medium text-gray-700 w-16">To:</span>
                  <span className="text-gray-900">
                    {selectedRecipients.length > 0 
                      ? selectedRecipients.map(r => `${r.name} <${r.email}>`).join(', ')
                      : emailData.to
                    }
                  </span>
                </div>
                <div className="flex">
                  <span className="font-medium text-gray-700 w-16">Subject:</span>
                  <span className="text-gray-900">{emailData.subject}</span>
                </div>
                {selectedRecipients.length > 0 && (
                  <div className="flex">
                    <span className="font-medium text-gray-700 w-16">Count:</span>
                    <span className="text-gray-900">
                      {selectedRecipients.length} recipient{selectedRecipients.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* Email Content */}
              <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                <div
                  className="email-content-preview-container"
                  style={{ lineHeight: 'normal' }}
                  dangerouslySetInnerHTML={{ __html: getPreviewContent() }}
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400 font-semibold"
              >
                Close Preview
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  handleSendEmail({ preventDefault: () => { } });
                }}
                disabled={sending}
                className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center font-semibold"
              >
                <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                {sending ? 'Sending...' : 'Send Now'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clear Recipients Confirmation Dialog */}
      <ConfirmClearRecipientsDialog
        isOpen={showClearConfirmDialog}
        onClose={() => setShowClearConfirmDialog(false)}
        onConfirm={handleClearAllRecipients}
        recipientCount={selectedRecipients.length}
      />
    </div>
  );
};

export default ComposeEmail;